<template>
  <!------------------------------------------
    This component displays the funding cut tool.
  -------------------------------------------->
  <div id="funding-tool">
    <PageTitle v-if="embedded === false && elimination === false && print === false" title="Funding Impact Calculator"/>

    <SideMarginsWrapper maxWidth="900">
      <!-- ABOUT -->
      <AboutResource v-if="embedded === false && elimination === false && print === false"
        class="mt-4"
        :techContact1="{ name: 'Jordan', email: 'laytonj@lsc.gov', department: 'ODGA' }"
      >
        <p>
          This page provides an interactive calculator for estimating the decrease in people LSC grantees could serve given a percentage or amount cut to LSC's appropriation. Embedded from here, it is also available on (and thus styled to match) LSC.gov.
        </p>
      </AboutResource>

      <div id="funding-tool-container">

          <!-- FUNDING TOOL CONTROLS -->
        <div v-if="!print">
          <div id="funding-tool-controls" class="lsc-dot-gov-styling no-print">
            <div class="select-header first-select-header">
              Select a new total LSC appropriation
            </div>
            <div class="controls-row select-and-slider first-controls" id="appropriation-inputs">
              <v-text-field
                color="#163f62"
                background-color="#fff"
                dense
                v-model="appropriationInput"
                @input="onAppropriationInput"
                @blur="commitAppropriation"
                @keyup.enter="commitAppropriation"
                type="text"
                outlined
                :prepend-icon="icons.mdiCurrencyUsd"
                :hint="'0 to ' + new Intl.NumberFormat().format(baseAppropriation * appropriationMaxAsTimes)"
                />
              <div class="slider-group">
                <div class="slider-value" :style="{ color: appropriation < baseAppropriation ? '#8B0000' : (appropriation === baseAppropriation ? '#5a5a5a' : '#006400')}">
                  {{ appropriation === baseAppropriation ? appropriationYear + ' appropriation' : new Intl.NumberFormat().format(appropriation) }}
                </div>
                <div class="slider-container">
                  <div class="background-squares">
                    <div class="left-square"></div>
                    <div class="right-square"></div>
                  </div>
                  <v-slider
                    v-model="appropriation"
                    color="#004986"
                    track-color="#004986"
                    :min="baseAppropriation * appropriationMinAsTimes"
                    :max="baseAppropriation * appropriationMaxAsTimes"
                    step="100000"
                    @change="onAppropriationSliderChange()"
                  />
                </div>
              </div>
            </div>
            <div class="select-header">
              Or select a percent change
            </div>
            <div class="controls-row select-and-slider" id="percent-inputs">
              <v-text-field
                color="#163f62"
                background-color="#fff"
                dense
                v-model="percentInput"
                @blur="commitPercent"
                @keyup.enter="commitPercent"
                type="text"
                outlined
                :prepend-icon="icons.mdiPercentOutline"
                :hint="percentChangeMin + ' to ' + percentChangeMax"
              />
              <div class="slider-group">
                <div class="slider-value" :style="{ color: percentChange < 0 ? '#8B0000' : (percentChange === 0 ? '#5a5a5a' : '#006400')}">
                    {{ percentChange === 0 ? 'no change' : (percentChange > 0 ? '+' + percentChange : percentChange) }}{{ percentChange === 0 ? '' : '%' }}
                  
                </div>
                <div class="slider-container">
                  <div class="background-squares">
                    <div class="left-square"></div>
                    <div class="right-square"></div>
                  </div>
                  <v-slider
                    v-model="percentChange"
                    color="#004986"
                    track-color="#004986"
                    :min="percentChangeMin"
                    :max="percentChangeMax"
                    step="1"
                    @change="onPercentSliderChange()"
                  />
                </div>
              </div>
            </div>
            <div class="controls-row geo-scopes">
                <div 
                  :class="['geo-scope-tab national-scope-tab', { active: selectedTab === 'national' }]"
                  @click="setSelectedTab('national')"
                >
                <v-btn
                  class="geo-scope-btn"
                  outlined
                  color="#8a8a8a"
                >
                    <span :style="{ color: selectedTab === 'national' ? '#163f62' : '#5a5a5a', width: '100%', textAlign: 'left' }">National</span>
                </v-btn>
              </div>
                <div 
                  :class="['geo-scope-tab state-scope-tab', { active: selectedTab === 'state' }]"
                  @click="setSelectedTab('state')"
                  >
                <v-autocomplete
                  v-model="selectedState"
                  :items="states"
                  color="#163f62"
                  :background-color="selectedTab === 'state' ? '#fff' : ''"
                  outlined
                  dense
                  label="State"
                  hint="Type to search"
                  persistent-hint
                ></v-autocomplete>
                </div>
                <div 
                  :class="['geo-scope-tab cd-scope-tab', { active: selectedTab === 'cd' }]"
                  @click="setSelectedTab('cd')"
                  >
                <v-autocomplete
                  v-model="selectedCongressionalDistrict"
                  :items="districts"
                  color="#163f62"
                  :background-color="selectedTab === 'cd' ? '#fff' : ''"
                  outlined
                  dense
                  label="District"
                  hint="Type to search"
                  persistent-hint
                ></v-autocomplete>
                </div>
            </div>
          </div>

          <br class="no-print"/>
          <br class="no-print"/>
        </div>

        <!-- DISPLAY THIS BEFORE DATA LOADS (SPINNER & OTHER INFO) -->
        <div v-if="(!toolData) && loading">
          <LoadingSpinner />
        </div>

        <!-- DIPLAY THIS CONTENT ONCE DATA ARE AVAILABLE -->
        <div v-if="toolData !== null && loading === false">

          <!-- WEB LAYOUT -->
          <div id="funding-tool-web-layout" v-if="!print && !elimination">
            <div class="grid-header-left grid-header" :class="{ 'print-version': print }">
              Last year, LSC grantees helped...
            </div>
            <div class="grid-header-right grid-header" :class="{ 'print-version': print }">
              <template v-if="percentChange < 0">
                With an appropriation of ${{ new Intl.NumberFormat().format(appropriation) }} &mdash; a {{ Math.abs(percentChange) }}% decrease &mdash; LSC grantees would help...
              </template>
              <template v-else-if="percentChange > 0">
                With an appropriation of ${{ new Intl.NumberFormat().format(appropriation) }} &mdash; a {{ percentChange }}% increase &mdash; LSC grantees would help...
              </template>
              <template v-else>
                <i>Select a new appropriation above to see the impact on the number of people LSC grantees can serve.</i>
              </template>
            </div>

            <!-- TOTAL -->
            <div class="grid-total-left grid-cell grid-cell-left">
              <div class="grid-icon-container">
                <img class="grid-icon" :src="require(`@/assets/images/funding_tool/people.png`)">
              </div>
              <div class="grid-data-point">
                <div class="data-point-main">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.total : '______' }}</span> people helped
                </div>
                <div class="data-point-sub">with their legal problems</div>
              </div>
            </div>
            <div class="grid-total-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
                {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
                {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point">
                <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.total_mult : '______' }}</span> {{ fewerOrMore }} people
                </div>
                <div class="data-point-sub">with their legal problems</div>
              </div>
            </div>

            <!-- INFO -->
            <div class="grid-info-left grid-cell grid-cell-left">
              <div class="grid-icon-container">
                <img class="grid-icon" :src="require(`@/assets/images/funding_tool/info.png`)">
              </div>
              <div class="grid-data-point">
                <div class="data-point-main">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.info : '______' }}</span> people</div>
                <div class="data-point-sub">access legal information and self-help resources</div>
              </div>
            </div>
            <div class="grid-info-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
                {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
                {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point">
                <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.info_mult : '______' }}</span> {{ fewerOrMore }} people
                </div>
                <div class="data-point-sub">access legal information and self-help resources</div>
              </div>
            </div>

            <!-- PEOPLE -->
            <div class="grid-people-left grid-cell grid-cell-left">
              <div class="grid-icon-container" :class="{ 'print-version': print }">
                <img class="grid-icon grid-icon-man" :src="require(`@/assets/images/funding_tool/man.png`)">
                <img class="grid-icon grid-icon-woman" :src="require(`@/assets/images/funding_tool/woman.png`)">
              </div>
              <div class="grid-data-point" :class="{ 'print-version': print }">
                <div class="data-point-main"><span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.people : '______' }}</span> people</div>
                <div class="data-point-sub">address civil legal problems in handled cases</div>
              </div>
            </div>
            
            <div class="grid-people-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
              {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
              {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point" :class="{ 'print-version': print }">
              <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.people_mult : '______' }}</span> {{ fewerOrMore }} people
              </div>
              <div class="data-point-sub">address civil legal problems in handled cases</div>
              </div>
            </div>

            <!-- CHILDREN -->
            <div class="grid-children-left grid-cell grid-cell-left">
              <div class="grid-icon-container" :class="{ 'print-version': print }">
                <img class="grid-icon grid-icon-child" :src="require(`@/assets/images/funding_tool/child.png`)">
              </div>
              <div class="grid-data-point" :class="{ 'print-version': print }">
                <div class="data-point-main"><span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.children : '______' }}</span> children</div>
              </div>
            </div>

            <div class="grid-children-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
                {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
                {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point" :class="{ 'print-version': print }">
                <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.children_mult : '______' }}</span> {{ fewerOrMore }} children
                </div>
              </div>
            </div>
            
            <!-- OLDER AMERICANS -->
            <div class="grid-older-left grid-cell grid-cell-left">
              <div class="grid-icon-container" :class="{ 'print-version': print }">
                <img class="grid-icon grid-icon-senior" :src="require(`@/assets/images/funding_tool/senior.png`)">
              </div>
              <div class="grid-data-point" :class="{ 'print-version': print }">
                <div class="data-point-main"><span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.older : '______' }}</span> older Americans</div>
              </div>
            </div>

            <div class="grid-older-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
                {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
                {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point" :class="{ 'print-version': print }">
                <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.older_mult : '______' }}</span> {{ fewerOrMore }} older Americans
                </div>
              </div>
            </div>

            <!-- VETERANS -->
            <div class="grid-veteran-left grid-cell grid-cell-left">
              <div class="grid-icon-container" :class="{ 'print-version': print }">
                <img class="grid-icon" :src="require(`@/assets/images/funding_tool/veteran.png`)">
              </div>
              <div class="grid-data-point">
                <div class="data-point-main"><span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.veteran : '______' }}</span> veterans</div>
              </div>
            </div>

            <div class="grid-veteran-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
                {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
                {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point">
                <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.veteran_mult : '______' }}</span> {{ fewerOrMore }} veterans
                </div>
              </div>
            </div>

            <!-- DV -->
            <div class="grid-dv-left grid-cell grid-cell-left">
              <div class="grid-icon-container" :class="{ 'print-version': print }">
                <img class="grid-icon" :src="require(`@/assets/images/funding_tool/dv.png`)">
              </div>
              <div class="grid-data-point">
                <div class="data-point-main"><span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.dv : '______' }}</span> people</div>
                <div class="data-point-sub">in <span class="dv-bold">domestic violence</span> related cases</div>
              </div>
            </div>
            
            <div class="grid-dv-right grid-cell grid-cell-right" :class="{ 'hide-for-no-change': percentChange === 0 }">
              <v-icon v-if="percentChange < 0" class="change-icon" color="#B10000" size="5rem">
                {{ icons.mdiArrowDownBold }}
              </v-icon>
              <v-icon v-else-if="percentChange > 0" class="change-icon" color="#599c59" size="5rem">
                {{ icons.mdiArrowUpBold }}
              </v-icon>
              <div class="grid-data-point">
                <div :class="['data-point-main data-point-main-right', { 'data-point-positive': percentChange > 0 }]">
                  <span>{{ selectedData.ppl_served_in_handled_cases ? selectedDataDictionary.dv_mult : '______' }}</span> {{ fewerOrMore }} people
                </div>
                <div class="data-point-sub">in domestic violence related cases</div>
              </div>
            </div>
          </div>

          <!-- SOURCES -->
          <div id="funding-tool-sources" v-if="!print && !elimination">
            <v-expansion-panels accordion>
              <v-expansion-panel>
                <v-expansion-panel-header class="sources-header">Sources</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="sources-category">
                    All estimates are based on LSC-eligible cases and other services as reported to LSC in the latest annual Grantee Activity Report.
                    <br><br>
                    <div class="sources-category-name">People helped with their legal problems</div>
                    <div class="sources-category-value">
                      A combination of people served in handled cases and people helped with education and information, as described below.
                    </div>
                  </div>
                  <div class="sources-category">
                    <div class="sources-category-name">People accessing legal information and self-help resources</div>
                    <div class="sources-category-value">
                      The number of people provided information or education in any setting multiplied by the average household size per closed case (nationally).
                    </div>
                  </div>
                  <div class="sources-category">
                    <div class="sources-category-name">People addressing civil legal problems in handled cases</div>
                    <div class="sources-category-value">
                      The number of people served in handled cases (closed cases and cases that were open as of the last day of the year), calculated as the number of handled cases multiplied by the average household size per closed case (nationally).
                    </div>
                  </div>
                  <div class="sources-category">
                    <div class="sources-category-name">Children</div>
                    <div class="sources-category-value">
                      The number of children served in handled cases, calculated as the number of handled cases multiplied by the average number of children per closed case (nationally).
                    </div>
                  </div>
                  <div class="sources-category">
                    <div class="sources-category-name">Older Americans</div>
                    <div class="sources-category-value">
                      The number of people 60 and over served in handled cases, calculated as the number of clients 60 and over in closed cases multiplied by the average number of adults per closed case (nationally) and by the national ratio of handled to closed cases.
                    </div>
                  </div>
                  <div class="sources-category">
                    <div class="sources-category-name">Veterans</div>
                    <div class="sources-category-value">
                      The number of veterans served in handled cases, calculated as the number of households with a veteran in closed cases multiplied by the national ratio of handled to closed cases.
                    </div>
                  </div>
                  <div class="sources-category">
                    <div class="sources-category-name">People in domestic violence related cases</div>
                    <div class="sources-category-value">
                      The number of domestic violence survivors in handled cases, calculated as the number of closed cases involving domestic violence multiplied by the national ratio of handled to closed cases.
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          <!----------------------------------------------------------------------------------------->
          <!----------------------------------------------------------------------------------------->
          <!----------------------------------------------------------------------------------------->
          <!----------------------------------------------------------------------------------------->
          <!----------------------------------------------------------------------------------------->
          <!----------------------------------------------------------------------------------------->
          
          <!-- PRINT VERSION -->
          <div id="funding-tool-elimination-layout" v-if="print && (elimination || cut)">
            <div id="elimination-header">
              <div id="elimination-main-title">
                <span v-if="elimination">Eliminating the Legal Services Corporation Harms our Communities and Families</span>
                <span v-else-if="cut && !selectedCongressionalDistrict && !selectedState">{{ ['8','11'].includes(Math.abs(percentChange).toString()) || Math.abs(percentChange).toString().startsWith('8') ? 'An' : 'A'}} {{ Math.abs(percentChange) }}% Cut to Legal Services Corporation Funding Would Leave Many Without Legal Help</span>
                <span v-else-if="cut">
                  {{ ['8','11'].includes(Math.abs(percentChange).toString()) || Math.abs(percentChange).toString().startsWith('8') ? 'An' : 'A'}} {{ Math.abs(percentChange) }}% Cut to Legal Services Corporation Funding in<br/>
                    <span v-if="selectedState">{{ selectedStateName.name }}</span>
                    <span v-else-if="selectedCongressionalDistrict">{{states.filter(s => s.value === selectedCongressionalDistrict.substring(0, 2))[0].text}}</span>
                 Would Leave Many Without Legal Help</span>
              </div>
            </div>
            <div id="elim-intro-text" class="elimination-normal-text">
              Each year, Legal Services Corporation (LSC) grantees provide essential civil legal aid to millions of low-income Americans. Proposals to <span v-if="elimination">eliminate LSC</span><span v-if="cut">cut LSC funding</span> would leave some of the most vulnerable members of our communities without help when they need it most.
            </div>
            <div class="elimination-subheader elim-subheader-bold" v-if="!selectedCongressionalDistrict && !selectedState">
              <span v-if="elimination">
                Without LSC funding, Americans will be left without legal help.
              </span>
              <span v-else-if="cut">
                The Impact of the Proposed Cut to LSC Funding in America
              </span>
            </div>
            <div v-else-if="cut" class="elimination-subheader elim-subheader-bold">
              The Impact of the Proposed Cut to LSC Funding in 
                <span v-if="selectedState">{{ selectedStateName.name }}</span>
                <span v-else-if="selectedCongressionalDistrict">{{states.filter(s => s.value === selectedCongressionalDistrict.substring(0, 2))[0].text}}'s {{ selectedDistrictStateNum.district }} District</span>
            </div>
            <div v-else-if="elimination" class="elimination-subheader">
              <span>Without LSC funding, </span>
                <span v-if="selectedCongressionalDistrict" class="districtOrStateNameEmphasis">
                  {{ states.filter(s => s.value === selectedCongressionalDistrict.substring(0, 2))[0].demonym }}s in the {{ selectedDistrictStateNum.district }} district
                </span>
                <span v-else-if="selectedState"><span class="districtOrStateNameEmphasis">{{ selectedStateName.demonym }}s</span></span>
                 will be left without legal help.
            </div>
            <div class="elimination-normal-text tighter-line-height">
              Low-income individuals and families rely on help from LSC-funded legal aid organizations to protect their homes, health and livelihoods. <span v-if="elimination">Eliminating LSC</span><span v-if="cut">Cutting funding to LSC</span> would <span v-if="elimination">cut off</span><span v-if="cut">take away</span> a lifeline &#8212; leaving thousands to face life-altering legal challenges alone. 
            </div>
            <div id="elimination-main-categories">
              <div class="elimination-category" id="elimination-people">
                <div class="elimination-icon-container">
                  <img class="elimination-icon" id="elim-people-icon" :src="require(`@/assets/images/funding_tool/people.png`)">
                </div>
                <div class="elimination-cat-text first-cat">
                  <span class="elimination-amount"><span v-if="elimination">{{ selectedDataDictionary.total }}</span><span v-if="cut">{{ selectedDataDictionary.total_mult }}</span> 
                    <span v-if="!selectedCongressionalDistrict && !selectedState"> Americans</span>
                    <span v-else> people</span>
                  </span>
                  <span id="elim-cat-row-1-description">
                    <span v-if="selectedCongressionalDistrict">
                      in the district
                    </span>
                    <span v-else-if="selectedState">in the state</span>
                    would lose access to vital legal assistance, including:</span></div>
              </div>
              <div id="elim-main-cat-row-2"> <!----->
                <div class="elimination-category" id="elimination-legal-help">
                  <div class="elimination-icon-container">
                    <img class="elimination-icon" :src="require(`@/assets/images/funding_tool/info.png`)">
                  </div>
                  <div class="elimination-cat-text">
                    <div class="elimination-amount">
                      <span v-if="elimination">{{ selectedDataDictionary.info }} people</span><span v-if="cut">{{ selectedDataDictionary.info_mult }}</span> people
                    </div>
                    <div>seeking legal information and guidance</div>
                  </div>
                </div>
                <div class="elimination-category" id="elimination-cases">
                  <div class="elimination-icon-container">
                    <img class="elimination-icon elimination-icon-man" :src="require(`@/assets/images/funding_tool/man.png`)">
                    <img class="elimination-icon elimination-icon-woman" :src="require(`@/assets/images/funding_tool/woman.png`)">
                  </div>
                  <div class="elimination-cat-text">
                    <div class="elimination-amount">
                      <span v-if="elimination">{{ selectedDataDictionary.people }}</span><span v-if="cut">{{ selectedDataDictionary.people_mult }}</span> people
                    </div>
                    <div>with active legal cases that would go unresolved</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="elimination-normal-text" id="among-those">
              Among the <span class="elimination-amount elim-amount-small">
                <span v-if="elimination">{{ selectedDataDictionary.people }}</span><span v-if="cut">{{ selectedDataDictionary.people_mult }}</span></span> people with active legal cases that would go unresolved:
            </div>
            <div id="elimination-categories-2">
              <div class="elimination-category">
                <div class="elimination-icon-container">
                  <img class="elimination-icon" id="elim-child-icon" :src="require(`@/assets/images/funding_tool/child.png`)">
                </div>
                <div class="elimination-cat-text">
                  <div class="elimination-amount">
                    <span v-if="elimination">{{ selectedDataDictionary.children }}</span><span v-if="cut">{{ selectedDataDictionary.children_mult }}</span> children
                  </div>
                  <div>would face greater instability at home, at school and in their health, due to unresolved civil legal issues affecting their families.</div>
                </div>
              </div>
              <div class="elimination-category">
                <div class="elimination-icon-container">
                  <img class="elimination-icon" :src="require(`@/assets/images/funding_tool/dv.png`)">
                </div>
                <div class="elimination-cat-text">
                  <div class="elimination-amount">
                    <span v-if="elimination">{{ selectedDataDictionary.dv }}</span><span v-if="cut">{{ selectedDataDictionary.dv_mult }}</span> survivors of domestic violence
                  </div>
                  <div>would lose access to legal protections to escape abuse and rebuild their lives &#8212; often with children in tow.</div>
                </div>
              </div>
              <div class="elimination-category">
                <div class="elimination-icon-container">
                  <img class="elimination-icon" :src="require(`@/assets/images/funding_tool/veteran.png`)">
                </div>
                <div class="elimination-cat-text">
                  <div class="elimination-amount">
                    <span v-if="elimination">{{ selectedDataDictionary.veteran }}</span><span v-if="cut">{{ selectedDataDictionary.veteran_mult }}</span> veterans and their families
                  </div>
                  <div>risk losing access to benefits, housing and healthcare they earned through service.</div>
                </div>
              </div>
              <div class="elimination-category">
                <div class="elimination-icon-container">
                  <img class="elimination-icon" :src="require(`@/assets/images/funding_tool/senior.png`)">
                </div>
                <div class="elimination-cat-text">
                  <div class="elimination-amount">
                    <span v-if="elimination">{{ selectedDataDictionary.older }}</span><span v-if="cut">{{ selectedDataDictionary.older_mult }}</span> older Americans 
                  </div>
                  <div>would lose access to help protecting their income, housing and safety from abuse and exploitation.</div>
                </div>
              </div>
            </div>
            <div class="elimination-subheader elim-subheader-red">
              Cutting legal aid costs more than it saves.
            </div>
            <div class="elimination-normal-text tighter-line-height">
              <span v-if="elimination">Eliminating</span><span v-if="cut">Cutting</span> LSC funding does not just harm individuals &mdash; it destabilizes entire communities and hurts local economies. When legal problems go unaddressed, families fall deeper into crisis, and the burden shifts to already-strained systems.
            </div>
            <div id="elim-roi">
              <div class="elim-roi-header">
                Every $1 Invested in Legal Aid Returns $7 in Economic Benefits
              </div>
              <div class="tighter-line-height">
                through reduced government spending, avoided court and emergency service costs, and improved employment outcomes. <span class="elim-roi-bold"><span v-if="elimination">Eliminating LSC</span><span v-if="cut">Cutting LSC funding</span> would <span v-if="elimination">erase</span><span v-if="cut">reduce</span> these benefits and undermine the efficiency of federal, state and local investments.</span>
              </div>
              <div v-if="elimination" class="elim-roi-learn-more">
                Learn more at <a href="https://www.lsc.gov/act">lsc.gov/act</a>
              </div>
              <div v-if="cut" class="elim-roi-learn-more">
                Learn more at <a href="https://www.lsc.gov/fundingcalculator">lsc.gov/fundingcalculator</a>
              </div>
            </div>
            <div id="elim-footer">
              <div id="elim-footer-text">
                LSC is the single largest funder of civil legal aid in th country. LSC promotes equal access to justice by funding high-quality civil legal assistance for low-income Americans. The figures presented reflect the number of people served by LSC grantees in LSC-eligible cases handled in {{ garYear }}.
              </div>
              <div id="elimination-lsc-logo-container">
                <img id="elimination-lsc-logo" :src="require(`@/assets/images/logo/lsc-logo-large.jpg`)" alt="LSC home">
              </div>
            </div>
          </div>
        </div>
      </div>
    </SideMarginsWrapper>
  </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import AboutResource from '@/components/common/AboutResource'
import SideMarginsWrapper from '@/components/layout/SideMarginsWrapper'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { initPostHeight } from '@/js/iframe_postmessage.js'
import { mdiCurrencyUsd, mdiPercentOutline, mdiArrowUpBold, mdiArrowDownBold } from '@mdi/js';

export default {
  name: 'fundingTool',

  components: { SideMarginsWrapper, AboutResource, LoadingSpinner, PageTitle },

  data () {
    return {
      appropriationYear: 2026,
      garYear: 2025,
      baseAppropriation: 540000000,
      baseBfAppropriation: 496300000,
      avgPercentBasicField: 92,
      appropriation: 540000000, // new approp, default to total base appropriation
      appropriationInput: '540,000,000',
      percentChange: 0, // default to no change
      percentInput: '0',
      percentChangeMin: -100,
      percentChangeMax: 100,
      appropriationMinAsTimes: 0, // baseAllocation times 0
      appropriationMaxAsTimes: 2, //baseAllocation times 2
      userIsTypingAppropriation: false, // track if user is editing the appropriation input so the resulting update to the percent change fields doesn't loop back and override it with a rounded value
      userIsTypingPercent: false,
      embedded: true,
      apiRoute: `${process.env.VUE_APP_BACKEND_NEW_DATA_API}`,
      loading: true,
      toolData: null,
      print: false,
      elimination: false, //Gets set to true if ?elimination=true in URL (special print version for 100% elimination)
      icons: {
        mdiCurrencyUsd: mdiCurrencyUsd,
        mdiPercentOutline: mdiPercentOutline,
        mdiArrowUpBold: mdiArrowUpBold,
        mdiArrowDownBold: mdiArrowDownBold
      },
      selectedTab: 'national',
      selectedState: null,
      selectedCongressionalDistrict: null,
      states: [ // https://en.wikipedia.org/wiki/List_of_demonyms_for_US_states_and_territories -- using first column
        {text: 'Alabama', value: '01', demonym: 'Alabamian'},
        {text: 'Alaska', value: '02', demonym: 'Alaskan'},
        // {text: 'American Samoa', value: '60', demonym: 'American Samoan'},
        {text: 'Arizona', value: '04', demonym: 'Arizonan'},
        {text: 'Arkansas', value: '05', demonym: 'Arkansan'},
        {text: 'California', value: '06', demonym: 'Californian'},
        {text: 'Colorado', value: '08', demonym: 'Coloradan'},
        {text: 'Connecticut', value: '09', demonym: 'Connecticuter'},
        {text: 'Delaware', value: '10', demonym: 'Delawarean'},
        {text: 'District of Columbia', value: '11', demonym: 'Washingtonian'},
        {text: 'Florida', value: '12', demonym: 'Floridian'},
        {text: 'Georgia', value: '13', demonym: 'Georgian'},
        // {text: 'Guam', value: '66', demonym: 'Guamanian'},
        {text: 'Hawaii', value: '15', demonym: 'Hawaii resident'},
        {text: 'Idaho', value: '16', demonym: 'Idahoan'},
        {text: 'Illinois', value: '17', demonym: 'Illinoisan'},
        {text: 'Indiana', value: '18', demonym: 'Hoosier'},
        {text: 'Iowa', value: '19', demonym: 'Iowan'},
        {text: 'Kansas', value: '20', demonym: 'Kansan'},
        {text: 'Kentucky', value: '21', demonym: 'Kentuckian'},
        {text: 'Louisiana', value: '22', demonym: 'Louisianian'},
        {text: 'Maine', value: '23', demonym: 'Mainer'},
        {text: 'Maryland', value: '24', demonym: 'Marylander'},
        {text: 'Massachusetts', value: '25', demonym: 'Massachusettsan'},
        {text: 'Michigan', value: '26', demonym: 'Michiganian'},
        {text: 'Minnesota', value: '27', demonym: 'Minnesotan'},
        {text: 'Mississipi', value: '28', demonym: 'Mississippian'},
        {text: 'Missouri', value: '29', demonym: 'Missourian'},
        {text: 'Montana', value: '30', demonym: 'Montanan'},
        {text: 'Nebraska', value: '31', demonym: 'Nebraskan'},
        {text: 'Nevada', value: '32', demonym: 'Nevadan'},
        {text: 'New Hampshire', value: '33', demonym: 'New Hampshirite'},
        {text: 'New Jersey', value: '34', demonym: 'New Jerseyan'},
        {text: 'New Mexico', value: '35', demonym: 'New Mexican'},
        {text: 'New York', value: '36', demonym: 'New Yorker'},
        {text: 'North Carolina', value: '37', demonym: 'North Carolinian'},
        {text: 'North Dakota', value: '38', demonym: 'North Dakotan'},
        // {text: 'Northern Mariana Islands', value: '69', demonym: 'Northern Marianan'},
        {text: 'Ohio', value: '39', demonym: 'Ohioan'},
        {text: 'Oklahoma', value: '40', demonym: 'Oklahoman'},
        {text: 'Oregon', value: '41', demonym: 'Oregonian'},
        {text: 'Pennsylvania', value: '42', demonym: 'Pennsylvanian'},
        // {text: 'Puerto Rico', value: '72', demonym: 'Puerto Rican'},
        {text: 'Rhode Island', value: '44', demonym: 'Rhode Islander'},
        {text: 'South Carolina', value: '45', demonym: 'South Carolinian'},
        {text: 'South Dakota', value: '46', demonym: 'South Dakotan'},
        {text: 'Tennessee', value: '47', demonym: 'Tennessean'},
        {text: 'Texas', value: '48', demonym: 'Texan'},
        {text: 'Utah', value: '49', demonym: 'Utahn'},
        {text: 'Vermont', value: '50', demonym: 'Vermonter'},
        // {text: 'Virgin Islands', value: '78', demonym: 'Virgin Islander'},
        {text: 'Virginia', value: '51', demonym: 'Virginian'},
        {text: 'Washington', value: '53', demonym: 'Washingtonian'},
        {text: 'West Virginia', value: '54', demonym: 'West Virginian'},
        {text: 'Wisconsin', value: '55', demonym: 'Wisconsinite'},
        {text: 'Wyoming', value: '56', demonym: 'Wyomingite'}
      ],
      numDistrictsByState: [ //118th or 119th Congress
        { st: 'Alabama', st_fips: '01', numDistricts: 7},
        { st: 'Alaska', st_fips: '02', numDistricts: 1 },
        { st: 'Arizona', st_fips: '04', numDistricts: 9 },
        { st: 'Arkansas', st_fips: '05', numDistricts: 4 },
        { st: 'California', st_fips: '06', numDistricts: 52 },
        { st: 'Colorado', st_fips: '08', numDistricts: 8 },
        { st: 'Connecticut', st_fips: '09', numDistricts: 5 },
        { st: 'Delaware', st_fips: '10', numDistricts: 1 },
        { st: 'District of Columbia', st_fips: '11', numDistricts: 1, nonVotingType: 'Delegate District (at Large)' },
        { st: 'Florida', st_fips: '12', numDistricts: 28 },
        { st: 'Georgia', st_fips: '13', numDistricts: 14 },
        { st: 'Hawaii', st_fips: '15', numDistricts: 2 },
        { st: 'Idaho', st_fips: '16', numDistricts: 2 },
        { st: 'Illinois', st_fips: '17', numDistricts: 17 },
        { st: 'Indiana', st_fips: '18', numDistricts: 9 },
        { st: 'Iowa', st_fips: '19', numDistricts: 4 },
        { st: 'Kansas', st_fips: '20', numDistricts: 4 },
        { st: 'Kentucky', st_fips: '21', numDistricts: 6 },
        { st: 'Louisiana', st_fips: '22', numDistricts: 6 },
        { st: 'Maine', st_fips: '23', numDistricts: 2 },
        { st: 'Maryland', st_fips: '24', numDistricts: 8 },
        { st: 'Massachusetts', st_fips: '25', numDistricts: 9 },
        { st: 'Michigan', st_fips: '26', numDistricts: 13 },
        { st: 'Minnesota', st_fips: '27', numDistricts: 8 },
        { st: 'Mississipi', st_fips: '28', numDistricts: 4 },
        { st: 'Missouri', st_fips: '29', numDistricts: 8 },
        { st: 'Montana', st_fips: '30', numDistricts: 2 },
        { st: 'Nebraska', st_fips: '31', numDistricts: 3 },
        { st: 'Nevada', st_fips: '32', numDistricts: 4 },
        { st: 'New Hampshire', st_fips: '33', numDistricts: 2 },
        { st: 'New Jersey', st_fips: '34', numDistricts: 12 },
        { st: 'New Mexico', st_fips: '35', numDistricts: 3 },
        { st: 'New York', st_fips: '36', numDistricts: 26 },
        { st: 'North Carolina', st_fips: '37', numDistricts: 14 },
        { st: 'North Dakota', st_fips: '38', numDistricts: 1 },
        { st: 'Ohio', st_fips: '39', numDistricts: 15 },
        { st: 'Oklahoma', st_fips: '40', numDistricts: 5 },
        { st: 'Oregon', st_fips: '41', numDistricts: 6 },
        { st: 'Pennsylvania', st_fips: '42', numDistricts: 17 },
        { st: 'Puerto Rico', st_fips: '72', numDistricts: 1, nonVotingType: 'Resident Commissioner District (at Large)' },
        { st: 'Rhode Island', st_fips: '44', numDistricts: 2 },
        { st: 'South Carolina', st_fips: '45', numDistricts: 7 },
        { st: 'South Dakota', st_fips: '46', numDistricts: 1 },
        { st: 'Tennessee', st_fips: '47', numDistricts: 9 },
        { st: 'Texas', st_fips: '48', numDistricts: 38 },
        { st: 'Utah', st_fips: '49', numDistricts: 4 },
        { st: 'Vermont', st_fips: '50', numDistricts: 1 },
        { st: 'Virginia', st_fips: '51', numDistricts: 11 },
        { st: 'Washington', st_fips: '53', numDistricts: 10 },
        { st: 'West Virginia', st_fips: '54', numDistricts: 2 },
        { st: 'Wisconsin', st_fips: '55', numDistricts: 8 },
        { st: 'Wyoming', st_fips: '56', numDistricts: 1 }
      ]
    }
  },

  computed: {
    cut () {
      return this.percentChange < 0
    },

    districts () {
      let d = []
      for (let state of this.numDistrictsByState) {
        d.push({ header: state.st })
        for (let i = 0; i < state.numDistricts; i++) {
          let label, districtNum
          if (state.nonVotingType) {
            label = state.nonVotingType
            districtNum = '98'
          } else if (state.numDistricts === 1) {
            label = 'At-large'
            districtNum = '00'
          } else {
            label = `${i+1}${this.nth(i+1)}`
            districtNum = `${String(i+1).padStart(2, '0')}`
          }
          d.push({ 
            text: `${state.st} ${label}`, 
            value: `${state.st_fips}${districtNum}`, 
            st_fips: state.st_fips, 
            st_name: `${state.st || ''}`,
            district_num_nth: `${label}`,
          })
        }
      }
      console.log('d',d)
      return d
    },

    selectedTabLabel () {
      if (this.selectedTab === 'national') {
        return 'Nationally'
      } else if (this.selectedTab === 'state' && this.selectedState !== null && this.selectedState !== undefined) {
        return 'In ' + this.states.filter(x => x.value === this.selectedState)[0].text
      } else if (this.selectedTab === 'cd' && this.selectedCongressionalDistrict !== null && this.selectedCongressionalDistrict !== undefined) {
        return 'In the ' + this.districts.filter(x => x.value === this.selectedCongressionalDistrict)[0].text + ' district'
      }
      return ""
    },

    selectedTabLabel2 () {
      if (this.selectedTab === 'National') {
        return ''
      } else if (this.selectedTab === 'state' && this.selectedState !== null && this.selectedState !== undefined) {
        return 'on ' + this.states.filter(x => x.value === this.selectedState)[0].text
      } else if (this.selectedTab === 'cd' && this.selectedCongressionalDistrict !== null && this.selectedCongressionalDistrict !== undefined) {
        return 'on the ' + this.districts.filter(x => x.value === this.selectedCongressionalDistrict)[0].text + ' district'
      }
      return ""
    },

    selectedStateName () {
      if (this.selectedState !== null && this.selectedState !== undefined) {
        let name = this.states.filter(x => x.value === this.selectedState)[0].text
        let demonym = this.states.filter(x => x.value === this.selectedState)[0].demonym
        return { name: name, demonym: demonym }
      }
      return {}
    },

    selectedDistrictStateNum () {
      if (this.selectedCongressionalDistrict !== null && this.selectedCongressionalDistrict !== undefined) {
        let st = this.districts.filter(x => x.value === this.selectedCongressionalDistrict)[0].st_name
        let cd_num = this.districts.filter(x => x.value === this.selectedCongressionalDistrict)[0].district_num_nth
        return {
          state: st,
          district: cd_num,
        }
      }
      return {}
    },

    selectedData () {
      if (this.toolData) {
        if (this.selectedTab === 'national') {
          return JSON.parse(JSON.stringify(this.toolData.filter(x => x.geo_scope === 'National')))[0]
        } else if (this.selectedTab === 'state' && this.selectedState !== null && this.selectedState !== undefined) {
          return JSON.parse(JSON.stringify(this.toolData.filter(x => x.fips === this.selectedState)))[0]
        } else if (this.selectedTab === 'cd' && this.selectedCongressionalDistrict !== null && this.selectedCongressionalDistrict !== undefined) {
          return JSON.parse(JSON.stringify(this.toolData.filter(x => x.fips === this.selectedCongressionalDistrict)))[0]
        }
      }
      return {}
    },

    selectedDataDictionary () {
      console.log('selectedData', this.selectedData)
      if (this.selectedData !== undefined && this.selectedData !== null) {
        return {
          'total': Intl.NumberFormat().format(this.selectedData.ppl_served_in_handled_cases + this.selectedData.ppl_given_legal_info),
          'people': Intl.NumberFormat().format(this.selectedData.ppl_served_in_handled_cases),
          'children': Intl.NumberFormat().format(this.selectedData.children_served_in_handled_cases),
          'older': Intl.NumberFormat().format(this.selectedData.older_served_in_handled_cases),
          'veteran': Intl.NumberFormat().format(this.selectedData.veteran_hh_served_in_handled_cases),
          'dv': Intl.NumberFormat().format(this.selectedData.dv_served_in_handled_cases),
          'info': Intl.NumberFormat().format(this.selectedData.ppl_given_legal_info),
          'total_mult': Intl.NumberFormat().format(
            Math.abs(
              Math.round(
                (this.selectedData.ppl_served_in_handled_cases + this.selectedData.ppl_given_legal_info) - (this.selectedData.ppl_served_in_handled_cases + this.selectedData.ppl_given_legal_info) * (1 + this.percentChange / 100)
              )
            )
          ),
          'people_mult': Intl.NumberFormat().format(Math.abs(Math.round(this.selectedData.ppl_served_in_handled_cases - this.selectedData.ppl_served_in_handled_cases * (1 + this.percentChange / 100)))),
          'children_mult': Intl.NumberFormat().format(Math.abs(Math.round(this.selectedData.children_served_in_handled_cases - this.selectedData.children_served_in_handled_cases * (1 + this.percentChange / 100)))),
          'older_mult': Intl.NumberFormat().format(Math.abs(Math.round(this.selectedData.older_served_in_handled_cases - this.selectedData.older_served_in_handled_cases * (1 + this.percentChange / 100)))),
          'veteran_mult': Intl.NumberFormat().format(Math.abs(Math.round(this.selectedData.veteran_hh_served_in_handled_cases - this.selectedData.veteran_hh_served_in_handled_cases * (1 + this.percentChange / 100)))),
          'dv_mult': Intl.NumberFormat().format(Math.abs(Math.round(this.selectedData.dv_served_in_handled_cases - this.selectedData.dv_served_in_handled_cases * (1 + this.percentChange / 100)))),
          'info_mult': Intl.NumberFormat().format(Math.abs(Math.round(this.selectedData.ppl_given_legal_info - this.selectedData.ppl_given_legal_info * (1 + this.percentChange / 100))))
        }
      } else {
        return {
            'people': null,
            'children': null,
            'older': null,
            'veteran': null,
            'dv': null,
            'info': null,
            'people_mult': null,
            'children_mult': null,
            'older_mult': null,
            'veteran_mult': null,
            'dv_mult': null,
            'info_mult': null
        }
      }
    },

    fewerOrMore () {
      if (this.percentChange < 0) {
        return 'fewer '
      } else if (this.percentChange > 0) {
        return ' more'
      } else {
        return ''
      }
    }
  },

  created () {
    this.initGetData()
  },

  mounted () {
    if (this.$route.query.print && this.$route.query.print === 'true') {
      this.print = true
    }
    if (this.$route.query.elimination && this.$route.query.elimination === 'true') {
      this.elimination = true
    }
    if (this.$route.query.appropriation) {
      this.appropriation = this.$route.query.appropriation
      this.appropriationInput = new Intl.NumberFormat().format(this.appropriation);
      this.updatePercentChange()
    }
    else if (this.$route.query.percentChange) {
      this.percentChange = this.$route.query.percentChange
      this.percentChange = Math.round(this.percentChange, 0)
      this.appropriation = Math.round(this.baseAppropriation * (1 + this.percentChange / 100))
      this.appropriationInput = new Intl.NumberFormat().format(this.appropriation);
    }
    initPostHeight('funding-tool')
  },
  
  methods: {
    nth(n){ //https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
      return["st","nd","rd"][((n+90)%100-10)%10-1]||"th"
    },

    dataPointAbbreviated (n) {
      if (n) {
        n = parseInt(n.replace(/,/g, ''), 10);
        if (n >= 1000000) {
        return (n / 1000000).toFixed(1) + ' million';
        } else if (n >= 10000) {
        return Math.floor(n / 1000) + ' thousand';
        } else {
        return 'no change';
        }
      }
    },

    async initGetData () {
      // Get data from AWS. One JSON file contains all data for national, states, and congressional districts
      let route = new URL(`${this.apiRoute}/public/project/funding-tool/funding_tool_data.json`)
      let d = await fetch(route, {method: 'GET', redirect: 'follow'})
        .then((response) => response.json())
        .catch((err) => {
          console.log(err)
          this.toolData = null
        })
      
      if (d && d !== undefined && d!== null) { 
        this.toolData = d
        this.loading = false
        console.log('data:', d)
      }
      return
    },

    onAppropriationInput() {
      this.userIsTypingAppropriation = true;
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.commitAppropriation();
      }, 500);
    },

    // Commit appropriation value when input is finished (after debounce)
    commitAppropriation() {
      this.userIsTypingAppropriation = false;
      let numericValue = parseFloat(this.appropriationInput.replace(/,/g, '')) || 0;

      // Ensure value is within the allowed range
      numericValue = Math.min(
        Math.max(numericValue, this.baseAppropriation * this.appropriationMinAsTimes),
        this.baseAppropriation * this.appropriationMaxAsTimes
      );

      this.appropriation = numericValue;
      this.updatePercentChange();
    },

    // Update percent change when appropriation changes
    updatePercentChange() {
      this.percentChange = Math.round(((this.appropriation - this.baseAppropriation) / this.baseAppropriation) * 100);
      this.percentInput = this.percentChange.toFixed(0);
    },

    // Handle changes to the appropriation slider
    onAppropriationSliderChange() {
      if (!this.userIsTypingAppropriation) {
        this.appropriationInput = new Intl.NumberFormat().format(this.appropriation);
        this.updatePercentChange();
      }
    },

    // Handle changes to the percent change input text field
    commitPercent() {
      this.userIsTypingPercent = false;
      let numericValue = parseFloat(this.percentInput) || 0;

      // Ensure value is within allowed range
      numericValue = Math.min(Math.max(numericValue, this.percentChangeMin), this.percentChangeMax);

      this.percentChange = numericValue;
      this.appropriation = this.baseAppropriation * (1 + numericValue / 100);
      this.appropriation = Math.round(this.appropriation);
      this.appropriationInput = new Intl.NumberFormat().format(this.appropriation);
    },

    // Handle changes to the percent change slider
    onPercentSliderChange() {
      if (!this.userIsTypingPercent) {
        this.percentInput = this.percentChange.toFixed(0);
        this.appropriation = this.baseAppropriation * (1 + this.percentChange / 100);
        this.appropriation = Math.round(this.appropriation);
        this.appropriationInput = new Intl.NumberFormat().format(this.appropriation);
      }
    },

    setSelectedTab(tab) {
      this.selectedTab = tab;
      if (tab !== 'state') {
        this.selectedState = null;
      }
      if (tab !== 'cd') {
        this.selectedCongressionalDistrict = null;
      }
    }
  },

  watch: {
    selectedState(to) {
      if (to) {
        window.top.postMessage({ state: to }, "*");
        const url = new URL(window.location);
        url.searchParams.set('state', to);
        window.history.replaceState({}, '', url);
      } else {
        window.top.postMessage({ state: null }, "*");
        const url = new URL(window.location);
        url.searchParams.delete('state');
        window.history.replaceState({}, '', url);
      }
    },

    selectedCongressionalDistrict(to) {
      if (to) {
        window.top.postMessage({ cd: to }, "*");
        const url = new URL(window.location);
        url.searchParams.set('cd', to);
        window.history.replaceState({}, '', url);
      } else {
        window.top.postMessage({ cd: null }, "*");
        const url = new URL(window.location);
        url.searchParams.delete('cd');
        window.history.replaceState({}, '', url);
      }
    },

    appropriation(to) {
      if (to !== null && to !== undefined) {
        window.top.postMessage({ appropriation: to }, "*");
        const url = new URL(window.location);
        url.searchParams.set('appropriation', to);
        window.history.replaceState({}, '', url);
      } else {
        window.top.postMessage({ appropriation: null }, "*");
        const url = new URL(window.location);
        url.searchParams.delete('appropriation');
        window.history.replaceState({}, '', url);
      }
    },

    percentChange(to) {
      if (to !== null && to !== undefined) {
        window.top.postMessage({ percentChange: to }, "*");
        const url = new URL(window.location);
        url.searchParams.set('percentChange', to);
        window.history.replaceState({}, '', url);
      } else {
        window.top.postMessage({ percentChange: null }, "*");
        const url = new URL(window.location);
        url.searchParams.delete('percentChange');
        window.history.replaceState({}, '', url);
      }
    },

    selectedTab(to) {
      const url = new URL(window.location);
      if (to === 'national') {
        window.top.postMessage({ state: null, cd: null }, "*");
        url.searchParams.delete('state');
        url.searchParams.delete('cd');
      }
      window.history.replaceState({}, '', url);
    },

    $route: { // Get whether the tool is embedded to hide certain elements on LSC.gov
      immediate: true,
      handler (to) {
        if (to.name !== null) { // ignore $route before full path is ready
          this.embedded = to.query.embed === "true"

          if (to.query.state) { // Precedent over CD if both are present
            this.selectedState = to.query.state
            this.selectedTab = 'state'
          } else if (to.query.cd) {
            this.selectedCongressionalDistrict = to.query.cd
            this.selectedTab = 'cd'
          }

          if (to.query.appropriation) { // Precedent over percent change if both are present
            let appropriationValue = parseFloat(to.query.appropriation)
            if (!isNaN(appropriationValue)) {
              appropriationValue = Math.min(
              Math.max(appropriationValue, this.baseAppropriation * this.appropriationMinAsTimes),
              this.baseAppropriation * this.appropriationMaxAsTimes
              )
              this.appropriation = appropriationValue
              this.appropriationInput = new Intl.NumberFormat().format(this.appropriation)
              this.updatePercentChange()
            }
          } else if (to.query.percentchange) {
            let percentChangeValue = parseFloat(to.query.percentchange)
            if (!isNaN(percentChangeValue)) {
              percentChangeValue = Math.min(Math.max(percentChangeValue, this.percentChangeMin), this.percentChangeMax)
              this.percentChange = percentChangeValue
              this.percentInput = this.percentChange.toFixed(0)
              this.appropriation = this.baseAppropriation * (1 + percentChangeValue / 100)
              this.appropriation = Math.round(this.appropriation)
              this.appropriationInput = new Intl.NumberFormat().format(this.appropriation)
            }
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/lsc-dot-gov.scss';
@import '@/scss/justice-gap-styles.scss';
@import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:ital,wght@0,100..900;1,100..900&family=Roboto+Serif:ital,opsz,wght@0,8..144,100..900;1,8..144,100..900&display=swap');

#print-version-switch {
  display: flex;
  justify-content: flex-end;
}
.no-selection-text {
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 0.625rem;
  background-color: #daeefd;
  font-family: Lato, sans-serif;
  font-size: 1.25rem;

  ul {
    margin-top: 0.25rem;
    font-size: 1rem;
  }
  
  li {
    padding: 0.325rem 0;
  }
}

#print-title {
  display: block;
  background-color: #064D88;
  color: #fff;
  font-family: "Lato", sans-serif;
  font-size: 1.5rem;
  height: 3.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0 0 1rem;
  border-top: 0.25rem solid #B10000;
}
#funding-tool-intro {
  padding: 0 0 0.5rem;
  color: $blue-charcoal;
  font-family: "Lato", sans-serif;
  font-size: 1rem;
  line-height: 1.75rem;

  &.print-version {
    font-size: 0.9rem;
    line-height: 1.625rem;
  }

  .highlight-numbers {
    color: $bright-red;
  }
  #calc-instructions {
    padding: 1rem 0;
    font-size: 0.9rem;
    margin: 0 2rem;
    text-align: center;
    color: #444444;
    font-weight: 400;
  }
}
#funding-tool-controls {
  display: flex;
  flex-direction: column;
  max-width: 40rem;
  justify-self: center;
  align-self: center;
  margin: 0 auto;
  background-color: $polar;
  border: 1px solid #d9d9d9;

  .select-header {
    padding: 0 0 0.5rem 1rem;
    color: $biscay;
    font-family: "Libre Franklin", sans-serif;
    font-weight: 400;
    font-size: 1.125rem;

    &.first-select-header {
      padding: 1rem 0 0.5rem 1rem;
    }
  }

  .controls-row {
    display: flex;
    justify-content: space-between;
    padding: 0.25rem 0 0 0;

    &.select-and-slider {
      padding: 0 1rem 0 1rem;
    }
  }
  .controls-row > * {
    flex: 1;
    margin: 0 0.5rem;
  }
  .slider-group {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin-top: -1rem;

    .slider-value {
      text-align: center;
      font-size: 0.825rem;
    }
  }

  .slider-container {
    position: relative;
    width: 100%;
    height: 50px; /* Adjust height as needed */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .background-squares {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1.75rem;
    display: flex;
    z-index: 0;
  }

  .left-square,
  .right-square {
    flex: 1;
    height: 1.75rem;
  }

  .left-square {
    background-color: rgba(223, 202, 202, 0.5); /* Adjust color as needed */
    border-right: 1px solid #5a5a5a; /* Right-side border */
    margin-right: 0.0125rem;
  }

  .right-square {
    background-color: rgba(224, 255, 207, 0.5); /* Adjust color as needed */
  }

  /* Ensure the v-slider appears above the squares */
  .v-slider {
    position: relative;
    z-index: 1;
  }

  .geo-scopes {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-top: 0;

    .geo-scope-tab {
      text-align: center;
      padding: 0.5rem;
      margin: 0;   
      height: 5rem; 

      button {
        height: 40px;
        font-family: "Libre Franklin", sans-serif;
        font-weight: 400;
        font-size: 1rem;
      }

      &.active {
        margin-top: -2px;
        border-left: 1px solid #d1d1d1;
        border-right: 1px solid #d1d1d1;
        border-bottom: 1px solid #d1d1d1;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.12);
        
        button.geo-scope-btn {
          background-color: #daeefd;
          border-width: 1px;
          border-color: #163f62;
          span {
            color: black;
          }
        }
      }
      &:not(.active) {
        background-color: #f2f2f2;
        border-top: 1px solid #d1d1d1;
        padding-top: 0.5rem;
      }
    }
    .geo-scope-btn {
      width: 100%;
    }
  }
}
#funding-tool-web-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'header-left header-right'
    'total-left total-right'
    'info-left info-right'
    'people-left people-right'
    'children-left children-right'
    'older-left older-right'
    'veteran-left veteran-right'
    'dv-left dv-right';
  gap: 0;
  font-size: 1rem;
  margin: 0 auto;

  @media (max-width: 674px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'header-left'
      'total-left'
      'info-left'
      'people-left'
      'children-left'
      'older-left'
      'veteran-left'
      'dv-left'
      'header-right'
      'total-right'
      'info-right'
      'people-right'
      'children-right'
      'older-right'
      'veteran-right'
      'dv-right';
  }

  .change-icon {
    padding-right: 1rem;
  }

  .grid-header {
    color: #fff;
    font-family: "Lato", sans-serif;
    font-size: 1.125rem;
    padding: 0.75rem;
    font-weight: bold;
    margin-bottom: 0.25rem;

    &.print-version {
      font-size: 1rem;
    }
  }
  .grid-header-left {
    grid-area: header-left;
    background-color: #064D88;
  }
  .grid-header-right {
    grid-area: header-right;
    background-color: #B10000;
  }
  .grid-cell {
    height: 6rem;
    display: flex;
    padding: 0.5rem 2rem 0.5rem 0.5rem;
    align-items: center;
    min-height: 3rem;

    &.hide-for-no-change {
      opacity: 0;
    }

    .grid-icon-container {
      height: 4rem;
      width: 5rem;
      min-width: 5rem;
      margin: 0 1rem;
      display: flex;
      justify-content: center;
      align-items: flex-end;

      &.print-version {
        height: 3.5rem;
      }

      .grid-icon {
        height: 100%;

        &.grid-icon-woman {
          padding-left: 0.325rem;
          height: 3.75rem;
          align-self: flex-end;
        }
        &.grid-icon-child {
          height: 2.5rem;
        }
        &.grid-icon-senior {
          height: 3.625rem;
        }
      }
    }
    .grid-data-point {
      display: flex;
      flex-direction: column;
      justify-items: center;

      .data-point-main {
        color: #064D88;
        font-family: "Lato", sans-serif;
        font-size: 1.25rem;
        font-weight: 700;

        &.data-point-main-right {
          color: #B10000;
        }
        &.data-point-positive {
          color: #599c59;
        }
      }
      .data-point-sub {
        font-family: "Lato", sans-serif;
        font-size: 0.9rem;
      }
      .dv-bold {
        color: #064D88;
        font-weight: 700;
      }
      &.print-version {
        .data-point-main {
          font-size: 1.125rem !important;
        }
      }
    }
  }
  .grid-total-left {
    grid-area: total-left;
  }
  .grid-total-right {
    grid-area: total-right;

  }
  .grid-people-left {
    grid-area: people-left;
    border-left: 2.5rem solid #fff;
  }
  .grid-people-right {
    grid-area: people-right;
  }
  .grid-children-left {
    grid-area: children-left;
    border-left: 5rem solid #fff;
  }
  .grid-children-right {
    grid-area: children-right;
  }
  .grid-older-left {
    grid-area: older-left;
    border-left: 5rem solid #fff;
  }
  .grid-older-right {
    grid-area: older-right;
  }
  .grid-veteran-left {
    grid-area: veteran-left;
    border-left: 5rem solid #fff;
  }
  .grid-veteran-right {
    grid-area: veteran-right;
  }
  .grid-dv-left {
    grid-area: dv-left;
    border-left: 5rem solid #fff;
  }
  .grid-dv-right {
    grid-area: dv-right;
  }
  .grid-info-left {
    grid-area: info-left;
    border-left: 2.5rem solid #fff;
  }
  .grid-info-right {
    grid-area: info-right;
  }
  .grid-cell { 
    border-bottom: 1px solid #e4e2e2;
  }
}
#funding-tool-sources {
  margin-top: 5rem;

  .v-expansion-panels {
    border: 1px solid #e0eefa;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    background-color: #f0f7fc;
  }
  .v-expansion-panel {
    background-color: inherit;
  }
  .v-expansion-panel::before {
    box-shadow: none;
  }
  .v-expansion-panel-header {
    color: #b31308;
    letter-spacing: 0.2rem;
    font-weight: 400;
  }
  .sources-header {
    color: #163f62;
    font-family: "Overlock", sans-serif;
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.8125rem;
    font-style: italic;
  }
  .sources-category {
    display: flex;
    flex-direction: column;;
    font-size: 0.95rem;
    margin-bottom: 1rem;
    flex-wrap: nowrap;
    
    .sources-category-name {
      font-size: 1rem;
      font-family: "Lato", sans-serif;
    }
    .sources-category-value {
      margin: 0.5rem 0 0.5rem 1rem;
    }
  }
}
#funding-tool-elimination-layout {
  max-width: 43rem;
  min-width: 43rem;
  max-height: 61.0625rem;
  margin: 0 auto;
  // border: 1px solid #d9d9d9; //temporary
  font-family: "Lato", sans-serif;
  font-size: 0.875rem;

  .elimination-normal-text, .elimination-cat-text {
    font-size: 0.825rem;
    margin-bottom: 0.25rem;
  }
  .tighter-line-height {
    line-height: 1.2rem;
  }
  .first-cat {
    margin-left: 0.75rem;
  }
  .elimination-cat-text:not(.first-cat) {
    display: flex;
    flex-direction: column;
  }
  #elim-intro-text {
    font-size: 0.825rem;
  }

  #elimination-header {
    // display: flex;
    // justify-content: space-between;
    // align-items: flex-start;
    // margin: 0 0 0.5rem 0;
    margin: 0 auto 0.5rem auto;
    width: 90%;
    font-weight: 300;
    font-size: 1.5rem;
    color:#B10000;
    font-family: "Franklin Serif", serif;
    text-align: center;
    line-height: 1.9rem;
  }
  .elimination-subheader {
    background-color: #064D88;
    padding: 0.25rem;
    margin: 0.825rem 0;
    font-size: 1.0625rem;
    font-family: "Franklin Serif", serif;
    color: #fff;
    text-align: center;

    &.elim-subheader-red {
      margin-top: 0;
      background-color: #B10000;
      font-weight: bold;
    }

    .districtOrStateNameEmphasis {
      font-weight: bold;
    }
  }
  .elim-subheader-bold{
    font-weight: bold;
  }
  #elimination-main-categories {
    background-color: #F0F7FC;
    border: 1px solid #95CEFF;
    padding: 1rem 0.25rem 0.25rem 1rem;
    font-size: 0.9rem;
    margin: 0.5rem 0 0.75rem 0;
  }
  #elim-cat-row-1-description {
    font-size: 1rem;
    font-weight: 300;
  }
  #elim-main-cat-row-2 {
    display: flex;
    flex-direction: row;
  }
  .elimination-category {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    padding-bottom: 0.125rem;

    .elimination-icon-container {
      height: 2.5rem;
      width: 3rem;
      min-width: 4rem;
      display: flex;
      padding-right: 0.25rem;
      align-items: flex-end;
      justify-content: center;

      .elimination-icon {
        height: 100%;

        &.grid-icon-woman {
          padding-left: 0.325rem;
          height: 3.75rem;
          align-self: flex-end;
        }
        &#elim-people-icon {
          height: 3rem;
          padding: 0.25rem 0 0 0.25rem;
        }
        &#elim-child-icon {
          height: 2rem;
        }
        &.grid-icon-senior {
          height: 3.625rem;
        }
      }
    }
  }
  #elimination-legal-help,#elimination-cases {
    margin-left: 4rem;
  }
  .elimination-amount {
    color:#B10000;
    padding-right: 0.125rem;
    font-family: "Franklin Serif", serif;
    font-size: 1.125rem;

    &.elim-amount-small {
      font-size: 0.875rem;
      padding: 0 0.125rem;
    }
  }
  #elimination-categories-2 {
    padding: 0 0.5rem;
    margin-bottom: 0.75rem;

    .elimination-category {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      margin-bottom: 0.5rem;
    }
  }
  #elim-roi {
    color: #fff;
    background-color: #064D88;
    margin-top: 0.75rem;
    padding: 0.5rem 0.75rem 1.5rem 0.75rem;
    position: relative;

    .elim-roi-header {
      font-size: 1.125rem;
      text-align: center;
      font-weight: bold;
      margin-bottom: 0.25rem;
    }
    .elim-roi-bold {
      font-weight: bold;
    }
    .elim-roi-learn-more {
      text-align: right;
      position: absolute;
      bottom: -1rem;
      left: 50%;
      transform: translate(-50%);
      background-color: #CCE6FF;
      color: black;
      font-style: normal;
      padding: 0.325rem 1rem;

      a {
        color: black;
        text-decoration: none;
      }
    }
  }
  #elim-footer {
    padding-top: 0.75rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;

    #elim-footer-text {
      font-size: 0.6rem;
      padding-top: 0.75rem;
      padding-right: 1rem;
      line-height: 1.3;
    }
  }
}
#elimination-lsc-logo {
  height: 47px;
  width: auto;
  margin: 0 auto;
  padding: 0;
}
</style>